if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initVariantImageFilter();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initVariantImageFilter() {
        if (this.dataset.variantImageFilter !== 'true') return;

        this.filterColors = (this.dataset.filterColors || '')
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);

        this.applyVariantImageFilter(this.dataset.selectedColorKey || '');

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const galleryId = this.id || '';
            const sectionId = galleryId.replace('MediaGallery-', '');
            if (event.data?.sectionId && event.data.sectionId !== sectionId) return;
            this.applyVariantImageFilter(this.getSelectedColorKey(event.data?.variant));
          });
        }
      }

      normalizeColorKey(value) {
        return String(value || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_');
      }

      altMatchesColor(alt, colorKey) {
        if (!alt || !colorKey) return false;
        const padded = `_${this.normalizeColorKey(alt)}_`;
        return padded.includes(`_${colorKey}_`);
      }

      isColorSpecificMedia(alt) {
        return this.filterColors.some((key) => this.altMatchesColor(alt, key));
      }

      shouldShowFilteredMedia(alt, selectedColorKey) {
        if (!this.isColorSpecificMedia(alt)) return true;
        return this.altMatchesColor(alt, selectedColorKey);
      }

      getSelectedColorKey(variant) {
        if (!variant) return this.normalizeColorKey(this.dataset.selectedColorKey || '');

        const optionValues = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
        for (const value of optionValues) {
          const key = this.normalizeColorKey(value);
          if (this.filterColors.includes(key)) return key;
        }

        return this.normalizeColorKey(optionValues[0] || variant.option1 || this.dataset.selectedColorKey || '');
      }

      applyVariantImageFilter(selectedColorKey) {
        const colorKey = this.normalizeColorKey(selectedColorKey);
        this.dataset.selectedColorKey = colorKey;

        const mediaItems = this.querySelectorAll('.product__media-item[data-media-alt], .product__media-item[data-media-id]');
        const thumbnails = this.querySelectorAll('.thumbnail-list__item');
        let firstVisibleMediaId = null;
        let visibleCount = 0;

        mediaItems.forEach((item) => {
          const alt = item.dataset.mediaAlt || item.querySelector('[alt]')?.getAttribute('alt') || '';
          const isVisible = this.shouldShowFilteredMedia(alt, colorKey);
          item.classList.toggle('variant-image-filter-hidden', !isVisible);
          if (isVisible) {
            visibleCount += 1;
            if (!firstVisibleMediaId) firstVisibleMediaId = item.dataset.mediaId;
          }
        });

        thumbnails.forEach((item) => {
          const alt = item.dataset.mediaAlt || item.querySelector('img')?.getAttribute('alt') || '';
          const isVisible = this.shouldShowFilteredMedia(alt, colorKey);
          item.classList.toggle('variant-image-filter-hidden', !isVisible);
        });

        this.filterModalMedia(colorKey);
        this.updateFilteredGalleryUi(visibleCount, firstVisibleMediaId);
      }

      filterModalMedia(selectedColorKey) {
        const sectionId = (this.id || '').replace('MediaGallery-', '');
        const modal = document.getElementById(`ProductModal-${sectionId}`);
        if (!modal) return;

        modal.querySelectorAll('[data-media-alt], img[alt]').forEach((element) => {
          const alt = element.dataset.mediaAlt || element.getAttribute('alt') || '';
          if (!alt && !element.dataset.mediaAlt) return;
          const isVisible = this.shouldShowFilteredMedia(alt, selectedColorKey);
          element.classList.toggle('variant-image-filter-hidden', !isVisible);
        });
      }

      updateFilteredGalleryUi(visibleCount, firstVisibleMediaId) {
        const totalEl = this.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = String(visibleCount || 1);

        const currentEl = this.querySelector('.slider-counter--current');
        if (currentEl) currentEl.textContent = '1';

        const dots = this.querySelectorAll('.thumbnail-dots .dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('variant-image-filter-hidden', index >= visibleCount);
          dot.classList.toggle('active', index === 0);
        });

        const activateFirstVisible = () => {
          if (firstVisibleMediaId) {
            const activeMedia = this.querySelector(`[data-media-id="${firstVisibleMediaId}"]`);
            this.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
              element.classList.toggle('is-active', element === activeMedia);
            });

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${firstVisibleMediaId}"]`);
              if (activeThumbnail?.querySelector('button')) {
                this.setActiveThumbnail(activeThumbnail);
              }
            }
          }

          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        };

        requestAnimationFrame(activateFirstVisible);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        // Load the video the first time it is selected.
        deferredMedia.loadContent(false);

        // When the video has already been loaded, loadContent() does not
        // start it again. Explicitly resume it when its thumbnail is clicked.
        const video = deferredMedia.querySelector('video');

        if (video) {
          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.debug('Product video autoplay was blocked:', error);
            });
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}